import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Calendar, Plus, ChevronRight } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { AlignmentAudit } from '../types/alignment';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

export function AlignmentAuditHistory() {
  const navigate = useNavigate();
  const [audits, setAudits] = useState<AlignmentAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAudit, setSelectedAudit] = useState<AlignmentAudit | null>(null);

  useEffect(() => {
    loadAudits();
  }, []);

  const loadAudits = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/');
      return;
    }

    const { data, error } = await supabase
      .from('alignment_audits')
      .select('*')
      .eq('user_id', user.id)
      .not('completed_at', 'is', null)
      .order('completed_at', { ascending: false });

    if (error) {
      console.error('Error loading audits:', error);
    } else {
      setAudits(data || []);
    }

    setLoading(false);
  };

  const getRatingAverage = (audit: AlignmentAudit) => {
    const ratings = [
      audit.mindset_rating,
      audit.outreach_rating,
      audit.delivery_rating,
      audit.ops_rating
    ].filter(r => r !== undefined) as number[];

    if (ratings.length === 0) return 0;
    return (ratings.reduce((sum, r) => sum + r, 0) / ratings.length).toFixed(1);
  };

  if (loading) {
    return (
      <ProtectedLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-gray-600">Loading your audit history...</div>
        </div>
      </ProtectedLayout>
    );
  }

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Alignment Audit History
              </h1>
              <p className="text-gray-700">
                Review your past audits and track your alignment journey.
              </p>
            </div>
            <Button
              onClick={() => navigate('/alignment-audit/start')}
              className="px-6 py-3"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Audit
            </Button>
          </div>

          {audits.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                No Audits Yet
              </h2>
              <p className="text-gray-600 mb-6">
                Start your first Alignment Audit to identify and align your business systems.
              </p>
              <Button onClick={() => navigate('/alignment-audit/start')}>
                Take Your First Audit
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {audits.map((audit) => (
                <div
                  key={audit.id}
                  className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => setSelectedAudit(selectedAudit?.id === audit.id ? null : audit)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                        <Target className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {audit.selected_area}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {audit.completed_at && format(new Date(audit.completed_at), 'MMM d, yyyy')}
                        </div>
                      </div>
                    </div>
                    <ChevronRight
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        selectedAudit?.id === audit.id ? 'rotate-90' : ''
                      }`}
                    />
                  </div>

                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="text-center">
                      <div className="text-xs text-gray-600 mb-1">Mindset</div>
                      <div className="text-lg font-bold text-teal-600">
                        {audit.mindset_rating || '-'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600 mb-1">Outreach</div>
                      <div className="text-lg font-bold text-teal-600">
                        {audit.outreach_rating || '-'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600 mb-1">Delivery</div>
                      <div className="text-lg font-bold text-teal-600">
                        {audit.delivery_rating || '-'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600 mb-1">Ops</div>
                      <div className="text-lg font-bold text-teal-600">
                        {audit.ops_rating || '-'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Average Certainty:</span>
                    <span className="font-bold text-teal-600">{getRatingAverage(audit)} / 5</span>
                  </div>

                  {selectedAudit?.id === audit.id && (
                    <div className="mt-6 pt-6 border-t space-y-4">
                      {audit.action && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Action Taken:</h4>
                          <p className="text-gray-700 bg-teal-50 p-3 rounded-lg">
                            {audit.action}
                          </p>
                        </div>
                      )}
                      {audit.goal && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Goal:</h4>
                          <p className="text-gray-700">{audit.goal}</p>
                        </div>
                      )}
                      {audit.celebrate && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Win Celebrated:</h4>
                          <p className="text-gray-700">{audit.celebrate}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ProtectedLayout>
  );
}
